import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/outline'
import type { MockInterviewSession } from '../types'
import { fetchMockInterviewById } from '../services/mockInterviewService'

type Props = {
  userId: string
}

const categoryColor: Record<string, string> = {
  technical: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  conceptual: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  behavioral: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
}

const difficultyLabel: Record<string, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function MockInterviewResultPage({ userId }: Props) {
  const { id } = useParams<{ id: string }>()
  const [session, setSession] = useState<MockInterviewSession | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id || !userId) return
    setIsLoading(true)
    fetchMockInterviewById(id, userId)
      .then(data => {
        setSession(data)
        if (!data) setError('Interview session not found')
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load interview'))
      .finally(() => setIsLoading(false))
  }, [id, userId])

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="h-8 w-48 bg-app-subtle rounded animate-pulse" />
        <div className="card p-6 space-y-4">
          <div className="h-24 bg-app-subtle rounded animate-pulse" />
          <div className="h-40 bg-app-subtle rounded animate-pulse" />
        </div>
      </div>
    )
  }

  if (error || !session) {
    return (
      <div className="mx-auto max-w-6xl">
        <div className="card p-6 text-center">
          <p className="text-rose-700 dark:text-rose-400 mb-4">{error || 'Interview session not found'}</p>
          <Link to="/interview" className="text-sm text-app-accent hover:text-app-accent-hover">
            Back to Mock Interviews
          </Link>
        </div>
      </div>
    )
  }

  // Category breakdown
  const answeredQuestions = session.questions.filter(q => q.score !== null)
  const categoryBreakdown: Record<string, { totalScore: number; count: number }> = {}
  for (const q of answeredQuestions) {
    if (!categoryBreakdown[q.category]) {
      categoryBreakdown[q.category] = { totalScore: 0, count: 0 }
    }
    categoryBreakdown[q.category].count++
    categoryBreakdown[q.category].totalScore += q.score || 0
  }

  const scoreColor = (s: number) =>
    s >= 80 ? 'text-emerald-700 dark:text-emerald-500' :
    s >= 60 ? 'text-amber-700 dark:text-amber-500' :
    'text-rose-700 dark:text-rose-500'

  const scoreBarColor = (s: number) =>
    s >= 80 ? 'bg-emerald-500' :
    s >= 60 ? 'bg-amber-500' :
    'bg-rose-500'

  const title = session.application_id
    ? 'Application-specific Interview'
    : session.general_role
      ? `${session.general_role} Interview`
      : 'Mock Interview'

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/interview"
          className="flex items-center gap-1.5 text-sm text-app-muted hover:text-app-ink transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Mock Interviews
        </Link>
      </div>

      {/* Score card */}
      <div className="card p-6 text-center space-y-4">
        <div className="flex items-center justify-center gap-2 text-app-muted">
          <MicrophoneIcon className="h-5 w-5" />
          <span className="text-sm font-medium">{title}</span>
        </div>

        <h1 className="text-2xl font-bold text-app-ink font-display">Interview Results</h1>

        <div className={`text-6xl font-bold ${scoreColor(session.score)}`}>
          {session.score}%
        </div>

        <p className="text-lg text-app-muted">
          {answeredQuestions.length} of {session.total_questions} questions answered
        </p>

        <div className="flex flex-wrap justify-center gap-4 text-sm text-app-muted">
          <span>Difficulty: <span className="text-app-ink font-medium">{difficultyLabel[session.difficulty]}</span></span>
          <span className="flex items-center gap-1">
            <ClockIcon className="h-4 w-4" />
            {formatTime(session.duration_seconds ?? 0)}
          </span>
          <span>
            {new Date(session.created_at).toLocaleDateString()}
          </span>
        </div>

        {/* Category breakdown */}
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          {Object.entries(categoryBreakdown).map(([cat, { totalScore, count }]) => {
            const avg = Math.round(totalScore / count)
            return (
              <div
                key={cat}
                className="flex items-center gap-2 rounded-lg border border-app-line px-3 py-1.5"
              >
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor[cat] || ''}`}>
                  {cat}
                </span>
                <span className={`text-sm font-medium ${scoreColor(avg)}`}>
                  {avg}%
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Questions review */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-app-ink font-display">Review Answers</h2>

        {session.questions.map((q, idx) => {
          const wasAnswered = q.user_answer !== null && q.user_answer !== undefined
          const wasSkipped = q.score === 0 && q.feedback === 'Question was skipped.'

          return (
            <div key={q.id} className="card p-5 space-y-3">
              {/* Question header */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  {wasSkipped ? (
                    <div className="h-5 w-5 rounded-full border-2 border-app-line" />
                  ) : q.score !== null && q.score >= 60 ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-700 dark:text-emerald-500" />
                  ) : (
                    <div className={`h-5 w-5 rounded-full ${q.score !== null ? scoreBarColor(q.score) : 'bg-app-line'}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-app-muted font-medium">Q{idx + 1}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor[q.category] || ''}`}>
                      {q.category}
                    </span>
                    {q.score !== null && (
                      <span className={`text-sm font-bold ${scoreColor(q.score)}`}>{q.score}%</span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-app-ink leading-relaxed">{q.text}</p>
                </div>
              </div>

              {/* Score bar */}
              {q.score !== null && !wasSkipped && (
                <div className="pl-8">
                  <div className="h-2 rounded-full bg-app-subtle overflow-hidden">
                    <div
                      className={`h-full rounded-full ${scoreBarColor(q.score)} transition-all`}
                      style={{ width: `${q.score}%` }}
                    />
                  </div>
                </div>
              )}

              {/* User's answer */}
              {wasAnswered && !wasSkipped && (
                <div className="pl-8">
                  <div className="rounded-lg border border-app-line bg-app-subtle px-4 py-3">
                    <p className="text-xs font-medium text-app-muted mb-1">Your answer:</p>
                    <p className="text-sm text-app-ink whitespace-pre-wrap">{q.user_answer}</p>
                  </div>
                </div>
              )}

              {/* Feedback */}
              {q.feedback && !wasSkipped && (
                <div className="pl-8">
                  <div className="rounded-lg border border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-950/20 px-4 py-3">
                    <p className="text-xs font-medium text-sky-700 dark:text-sky-400 mb-1">Feedback:</p>
                    <p className="text-sm text-sky-900 dark:text-sky-200">{q.feedback}</p>
                  </div>
                </div>
              )}

              {/* Strengths */}
              {q.strengths.length > 0 && (
                <div className="pl-8">
                  <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400 mb-1">Strengths:</p>
                  <ul className="space-y-1">
                    {q.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-app-ink flex items-start gap-2">
                        <span className="text-emerald-700 dark:text-emerald-500 mt-0.5">✓</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {q.improvements.length > 0 && (
                <div className="pl-8">
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">Areas to improve:</p>
                  <ul className="space-y-1">
                    {q.improvements.map((s, i) => (
                      <li key={i} className="text-sm text-app-ink flex items-start gap-2">
                        <span className="text-amber-700 dark:text-amber-500 mt-0.5">→</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sample answer hint */}
              {q.sample_answer_hint && (
                <div className="pl-8">
                  <div className="rounded-lg border border-app-line bg-app-subtle px-4 py-3">
                    <p className="text-xs font-medium text-app-muted mb-1">Hint — ideal answer includes:</p>
                    <p className="text-sm text-app-muted italic">{q.sample_answer_hint}</p>
                  </div>
                </div>
              )}

              {wasSkipped && (
                <p className="text-xs text-app-muted pl-8 italic">Skipped</p>
              )}
            </div>
          )
        })}
      </div>

      {/* Bottom actions */}
      <div className="flex justify-center gap-3 pb-8">
        <Link
          to="/interview"
          className="rounded-lg border border-app-line px-5 py-2.5 text-sm font-medium text-app-ink hover:bg-app-subtle transition-colors"
        >
          Back to Mock Interviews
        </Link>
        <Link
          to="/interview"
          className="rounded-lg bg-app-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-app-primary-hover transition-colors"
        >
          Start Another Interview
        </Link>
      </div>
    </div>
  )
}
